import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/articles/${slug}`);
        if (mounted) setArticle(data);
      } catch (err) {
        if (mounted)
          setError(err?.response?.data?.detail || "Articolo non trovato");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    document.title = `${article.title} | Studio Legale Ricciardi`;
    if (metaDesc && article.excerpt) {
      metaDesc.setAttribute("content", article.excerpt);
    }
    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc !== null && prevDesc !== undefined) {
        metaDesc.setAttribute("content", prevDesc);
      }
    };
  }, [article]);

  return (
    <div className="min-h-screen" data-testid="article-detail-page">
      <Navbar />

      {/* DARK HERO */}
      <section className="relative bg-carbon text-warm pt-32 lg:pt-40 pb-16 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-[0.18] pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8">
          <Link
            to="/approfondimenti"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-[#C5A880] hover:text-[#D5B890] mb-12"
            data-testid="article-back-list"
          >
            <ArrowLeft size={14} /> Tutti gli approfondimenti
          </Link>

          {loading && (
            <div className="flex items-center gap-3 text-[#FAF9F6]/60">
              <Loader2 size={16} className="animate-spin" /> Caricamento…
            </div>
          )}
          {!loading && error && (
            <div
              className="border border-[#FAF9F6]/10 p-8 text-[#FAF9F6]/70"
              data-testid="article-error"
            >
              {error}
            </div>
          )}
          {!loading && article && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="flex items-center gap-3 mb-6 text-[10px] tracking-[0.22em] uppercase font-semibold">
                <span className="text-[#C5A880]">
                  {article.tag || "Nota"}
                </span>
                <span className="text-[#FAF9F6]/30">·</span>
                <span className="text-[#FAF9F6]/55">
                  {formatDate(article.published_at)}
                </span>
                <span className="text-[#FAF9F6]/30">·</span>
                <span className="text-[#FAF9F6]/55">
                  {article.reading_time} min
                </span>
              </div>
              <h1 className="font-display text-4xl lg:text-6xl font-medium leading-[1.02] tracking-tight">
                {article.title}
              </h1>
              {article.excerpt && (
                <p className="mt-6 text-lg lg:text-xl text-[#FAF9F6]/75 leading-relaxed font-light">
                  {article.excerpt}
                </p>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* COVER IMAGE bridging dark → light */}
      {!loading && article?.cover_image && (
        <div className="relative bg-gradient-to-b from-[#0A0A0A] from-50% to-[#EAECE7] to-50%">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="relative border border-[#FAF9F6]/10">
              <img
                src={article.cover_image}
                alt={article.title}
                className="block w-full h-auto"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      )}

      {/* LIGHT ARTICLE BODY */}
      <section className="relative bg-[#EAECE7] text-[#0A0A0A] pt-16 lg:pt-20 pb-24 lg:pb-32">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          {!loading && article && (
            <motion.article
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              data-testid="article-content"
            >
              <div
                lang="it"
                className="prose-article prose-article-light"
                data-testid="article-body"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {article.content}
                </ReactMarkdown>
              </div>

              <div className="mt-20 pt-10 border-t border-[#0A0A0A]/10">
                <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#1B3B36] mb-3">
                  Studio Legale Ricciardi
                </div>
                <p className="text-sm text-[#0A0A0A]/65 leading-relaxed max-w-xl">
                  Contenuto redatto a scopo informativo. Non costituisce
                  parere legale personalizzato. Per una valutazione dedicata
                  al tuo caso, prenota un confronto operativo.
                </p>
                <Link
                  to="/#engage"
                  className="mt-6 inline-flex items-center gap-3 bg-[#1B3B36] text-[#FAF9F6] hover:bg-[#0A0A0A] transition-colors px-6 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold"
                  data-testid="article-cta-confronto"
                >
                  Prenota un confronto
                </Link>
              </div>
            </motion.article>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
